import {
  Button,
  ButtonGroup,
  type ButtonProps,
  Modal as CModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  type ModalContentProps,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
} from "@chakra-ui/react";
import { type PropsWithChildren, type ReactNode, useMemo } from "react";

import { Heading } from "@equidefi/ui";

type ModalButton = ButtonProps & {
  label: string;
  action?: () => void;
};

export type TModalProps<T> = ModalProps & {
  title?: string;
  isOpen: boolean;
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  buttons?: ModalButton[];
  children?: ReactNode;
  Header?: ReactNode;
  contentProps?: ModalContentProps;
};

export const Modal = <T,>({
  title,
  isOpen = false,
  children,
  buttons,
  Header,
  onClose,
  onSubmit = () => {},
  contentProps = {},
  ...rest
}: PropsWithChildren<TModalProps<T>>): React.ReactElement => {
  const Buttons = useMemo(() => {
    if (!buttons || !buttons.length) return null;

    return buttons.map(({ label, action, ...rest }) => (
      <Button
        onClick={action}
        aria-label={label}
        key={label}
        px={6}
        fontWeight="semibold"
        fontSize="md"
        colorScheme={rest.colorScheme ?? "purple"}
        variant={rest.variant ?? "solid"}
        transition=".1s"
        {...rest}>
        {label}
      </Button>
    ));
  }, [buttons]);

  return (
    <CModal
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      motionPreset="slideInBottom"
      isCentered
      {...rest}>
      <ModalOverlay backdropFilter="blur(8px)" />

      <ModalContent
        p={5}
        maxW={{ base: "full", md: contentProps?.maxW ?? "640px" }}
        w={{ base: "full", md: contentProps?.w ?? "640px" }}
        mx={{ base: "15px", md: "auto" }}
        borderRadius="xl"
        {...contentProps}>
        <form onSubmit={onSubmit} noValidate>
          <ModalHeader p="0">
            {Header || (
              <Heading textStyle="h2" fontWeight="bold" fontSize="20px" mb="0">
                {title}
              </Heading>
            )}
            <ModalCloseButton />
          </ModalHeader>

          <ModalBody p="0" py="5">
            {children}
          </ModalBody>

          <ModalFooter p="0" justifyContent="start" w="full">
            <ButtonGroup w="full" gap={3}>
              {Buttons}
            </ButtonGroup>
          </ModalFooter>
        </form>
      </ModalContent>
    </CModal>
  );
};
